import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import JsonLd from './JsonLd';

// Same lightweight meta updater used elsewhere (Blog.js).
const setMeta = (selector, attr, value) => {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(selector);
  if (!el) {
    const [tag, ...rest] = selector.split('[');
    el = document.createElement(tag);
    if (rest.length) {
      const m = rest.join('[').match(/(\w+)=["']([^"']+)["']/);
      if (m) el.setAttribute(m[1], m[2]);
    }
    document.head.appendChild(el);
  }
  if (attr === 'text') el.textContent = value || '';
  else el.setAttribute(attr, value || '');
};

/**
 * Shared chrome for every page under /tools — page-level <title>,
 * canonical, header bar with breadcrumb, and a soft "Made by Fantastic
 * Forms" footer CTA that drives signup conversions.
 *
 * Each tool just renders its widget + supporting copy as children;
 * the shell handles SEO, layout, and conversion footer.
 */
export default function ToolShell({ slug, title, tagline, h1, children }) {
  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', 'content', tagline);
    setMeta('link[rel="canonical"]', 'href', `https://fantasticforms.com/tools/${slug}`);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', tagline);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:url"]', 'content', `https://fantasticforms.com/tools/${slug}`);
    window.scrollTo(0, 0);
  }, [slug, title, tagline]);

  // JSON-LD: every tool is a free WebApplication. Google occasionally
  // picks these up as rich results for "free tool" / "online utility"
  // queries; even when it doesn't, the structured data clarifies the
  // surface to LLM crawlers (ChatGPT, Perplexity).
  const url = `https://fantasticforms.com/tools/${slug}`;
  const webAppLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: h1 || title,
    headline: title,
    description: tagline,
    url,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any (browser-based)',
    browserRequirements: 'Requires JavaScript. Modern browser.',
    isAccessibleForFree: true,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    creator: {
      '@type': 'Organization',
      name: 'Fantastic Forms',
      url: 'https://fantasticforms.com',
      logo: 'https://fantasticforms.com/logo.png',
    },
  }), [slug, title, tagline, h1, url]);

  const breadcrumbLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',       item: 'https://fantasticforms.com/' },
      { '@type': 'ListItem', position: 2, name: 'Free tools', item: 'https://fantasticforms.com/tools' },
      { '@type': 'ListItem', position: 3, name: h1 || title,  item: url },
    ],
  }), [h1, title, url]);

  return (
    <div className="min-h-screen bg-zinc-50">
      <JsonLd id={`tool-${slug}-app`} data={webAppLd} />
      <JsonLd id={`tool-${slug}-bc`}  data={breadcrumbLd} />
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/tools" className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900">
            <ArrowLeft className="w-4 h-4" />
            All free tools
          </Link>
          <Link to="/" className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5" data-testid="tool-shell-home">
            <span>Fantastic Forms</span>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 mb-3">{h1}</h1>
        <p className="text-base sm:text-lg text-zinc-600 mb-8 leading-relaxed">{tagline}</p>
        <div className="space-y-8">{children}</div>

        {/* Conversion footer — every tool page sells the SaaS subtly */}
        <section className="mt-14 rounded-xl border border-zinc-200 bg-white p-6 sm:p-8" data-testid="tool-shell-cta">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#FE6A6B] flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-zinc-900 mb-1">Built by the team behind Fantastic Forms</h2>
              <p className="text-sm text-zinc-600 mb-3">
                We make the form builder Tally and Typeform users switch to. Every feature free, one paid plan at $99/month.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#FE6A6B] hover:underline"
                data-testid="tool-shell-cta-link"
              >
                Try Fantastic Forms free →
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
